/* eslint-disable no-underscore-dangle */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DestinationSearchBlockOffer } from 'src/modules/cms';

import { safeSelect, StateSelector } from '@fcom/core/selectors/selector-utils';
import { isPresent } from '@fcom/core/utils';
import { GlobalBookingTravelClass } from '@fcom/core';
import { TripType } from '@fcom/core/interfaces';
import {
  DestinationSearchTripDates,
  DestinationSearchItem,
  DestinationSearchFeatureState,
  DESTINATION_SEARCH_FILTER_STATE_FEATURE_NAME,
  DestinationSearchFilters,
  DestinationSearchState,
} from '@fcom/destination-search/interfaces';

import { defaultFilters } from '../reducers';

const _selectDestinationSearchFilterStates = createFeatureSelector<
  DestinationSearchFeatureState,
  DestinationSearchState
>(DESTINATION_SEARCH_FILTER_STATE_FEATURE_NAME);

const _selectTripDates = (id) =>
  createSelector(_filterState(id), (state: DestinationSearchFilters) => {
    const { departureDate, returnDate } = state.tripDates;
    const bothDatesArePresent = isPresent(departureDate) && isPresent(returnDate);
    const bothDatesAreUndefined = !isPresent(departureDate) && !isPresent(returnDate);
    const oneWayWithOneDatePresent = state.tripType === TripType.ONEWAY && isPresent(departureDate);

    return bothDatesArePresent || bothDatesAreUndefined || oneWayWithOneDatePresent
      ? state.tripDates
      : defaultFilters.tripDates;
  });

const _selectTripType = (id) => createSelector(_filterState(id), (state: DestinationSearchFilters) => state.tripType);

const _selectTravelClass = (id) =>
  createSelector(_filterState(id), (state: DestinationSearchFilters) => state.travelClass);

const _selectSelectedDesinations = (id) =>
  createSelector(_filterState(id), (state: DestinationSearchFilters) => state.selectedDestinations);

const _selectFromLocationCode = (id) =>
  createSelector(_filterState(id), (state: DestinationSearchFilters) => state.fromLocationCode);

const _selectRestrictToDestinationsState = (id) =>
  createSelector(_filterState(id), (state: DestinationSearchFilters) => state.restrictToDestinations);

const _filterState = (id) =>
  createSelector(
    _selectDestinationSearchFilterStates,
    (state: DestinationSearchState) => state?.[id] ?? defaultFilters
  );

const _allFilterStates = () =>
  createSelector(_selectDestinationSearchFilterStates, (state: DestinationSearchState) => state);

const _selectDestinationsBeforeFiltering = (id: string) =>
  createSelector(_filterState(id), (state: DestinationSearchFilters) => state.destinationsBeforeFiltering);

const _selectRoundTripPrices = (id: string) =>
  createSelector(_filterState(id), (state: DestinationSearchFilters) => state.roundTripPrices);

export const allFilterStates = (): StateSelector<DestinationSearchFeatureState, DestinationSearchState> =>
  safeSelect(_allFilterStates());

export const filterState = (id: string): StateSelector<DestinationSearchFeatureState, DestinationSearchFilters> =>
  safeSelect(_filterState(id));

export const tripDatesState = (id: string): StateSelector<DestinationSearchFeatureState, DestinationSearchTripDates> =>
  safeSelect(_selectTripDates(id));

export const tripTypeState = (id: string): StateSelector<DestinationSearchFeatureState, TripType> =>
  safeSelect(_selectTripType(id));

export const travelClassState = (id: string): StateSelector<DestinationSearchFeatureState, GlobalBookingTravelClass> =>
  safeSelect(_selectTravelClass(id));

export const fromLocationCodeState = (id: string): StateSelector<DestinationSearchFeatureState, string> =>
  safeSelect(_selectFromLocationCode(id));

export const selectedDestinationsState = (
  id: string
): StateSelector<DestinationSearchFeatureState, DestinationSearchItem[]> => safeSelect(_selectSelectedDesinations(id));

export const restrictToDestinationsState = (id: string): StateSelector<DestinationSearchFeatureState, string[]> =>
  safeSelect(_selectRestrictToDestinationsState(id));

export const destinationsBeforeFilteringState = (
  id: string
): StateSelector<DestinationSearchFeatureState, DestinationSearchBlockOffer[]> =>
  safeSelect(_selectDestinationsBeforeFiltering(id));

export const roundTripPricesState = (id: string): StateSelector<DestinationSearchFeatureState, boolean> =>
  safeSelect(_selectRoundTripPrices(id));
